// Kopfzeile des Verlaufs im Gruppenfall: Name + Mitgliederkurzliste, dahinter ein Zahnrad
// für die Verwaltung. Team-Chats sind unveränderlich — ihre Mitglieder kommen aus der
// Team-Verwaltung (team_members), deshalb bietet dieser Baustein für sie konsequent keine
// Umbenennen-/Hinzufügen-/Entfernen-/Verlassen-Knöpfe an.
import { useEffect, useState } from 'react';
import { CaretLeft, GearSix, UsersThree, X } from '@phosphor-icons/react';
import { FARBE } from '../../pille-stil';
import {
  AUFBEWAHRUNG_TAGE, gruppenName, kollegeName, type ChatGruppe, type Kollege,
} from '../../../../lib/chat/nachrichten';
import { benenneGruppeUm, entferneMitglied, fuegeMitgliedHinzu } from '../../../../lib/chat/api';
import { Avatar } from './Avatar';

export function GruppenKopf({ gruppe, kollegen, meineId, onGeaendert, onVerlassen, onFehler, onZurueck }: {
  gruppe: ChatGruppe;
  kollegen: Kollege[];
  meineId: string;
  onGeaendert: () => void;
  onVerlassen: () => void;
  onFehler?: (msg: string) => void;
  onZurueck?: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState(gruppe.name || '');
  const [laedt, setLaedt] = useState(false);

  // Von außen umbenannt (anderes Mitglied) → Feld nachziehen, sonst überschreibt der nächste
  // eigene Speichern-Klick den fremden Stand wieder mit dem alten Namen.
  useEffect(() => { setName(gruppe.name || ''); }, [gruppe.id, gruppe.name]);

  const istTeamChat = gruppe.team_id !== null;
  // Gleiche Bedingung wie chat_gruppen_aendern: Gründerin, oder jedes Mitglied,
  // wenn die Gründerin nicht mehr existiert (erstellt_von auf null gesetzt).
  const darfVerwalten = !istTeamChat
    && (gruppe.erstellt_von === meineId || gruppe.erstellt_von === null);
  const darfVerlassen = !istTeamChat;   // austreten darf jedes Mitglied

  const namen = new Map(kollegen.map((k) => [k.id, k]));
  const mitgliedName = (id: string) => (id === meineId ? 'Ich' : kollegeName(namen.get(id)));
  const mitgliedNamen = gruppe.mitglieder.map(mitgliedName);
  const kurzListe = mitgliedNamen.length > 4
    ? `${mitgliedNamen.slice(0, 3).join(', ')} +${mitgliedNamen.length - 3} weitere`
    : mitgliedNamen.join(', ');
  const hinzufuegbar = kollegen.filter((k) => !gruppe.mitglieder.includes(k.id));

  async function speichern() {
    if (!name.trim() || laedt) return;
    setLaedt(true);
    const ok = await benenneGruppeUm(gruppe.id, name);
    setLaedt(false);
    if (ok) onGeaendert();
    else onFehler?.('Gruppe konnte nicht umbenannt werden.');
  }

  async function hinzufuegen(id: string) {
    if (laedt) return;
    setLaedt(true);
    const ok = await fuegeMitgliedHinzu(gruppe.id, id);
    setLaedt(false);
    if (ok) onGeaendert();
    else onFehler?.('Mitglied konnte nicht hinzugefügt werden.');
  }

  async function entfernen(id: string) {
    if (laedt) return;
    setLaedt(true);
    const ok = await entferneMitglied(gruppe.id, id);
    setLaedt(false);
    if (ok) onGeaendert();
    else onFehler?.('Mitglied konnte nicht entfernt werden.');
  }

  async function verlassen() {
    if (!darfVerlassen || laedt) return;
    setLaedt(true);
    // Verlassen ist technisch ein Entfernen der eigenen Mitgliedschaft — dieselbe Funktion,
    // die auch beim Team-Chat bewusst scheitert (siehe entferneMitglied).
    const ok = await entferneMitglied(gruppe.id, meineId);
    setLaedt(false);
    if (ok) onVerlassen();
    else onFehler?.('Gruppe konnte nicht verlassen werden.');
  }

  return (
    <div style={{ borderBottom: '0.5px solid rgba(255,255,255,0.1)' }}>
      <div className="flex items-center" style={{ gap: 10, padding: '8px 12px' }}>
        {onZurueck && (
          <button
            type="button"
            onClick={onZurueck}
            className="flex shrink-0 cursor-pointer items-center sm:hidden"
            style={{ height: 36, gap: 2, border: 'none', background: 'transparent', color: FARBE.gold, fontFamily: 'inherit', fontSize: 15 }}
            aria-label="Zurück zu den Chats"
          >
            <CaretLeft size={18} weight="bold" /> Chats
          </button>
        )}
        <span className="relative shrink-0">
          <Avatar name={gruppenName(gruppe)} bild={null} groesse={36} />
          {gruppe.team_id && (
            <span className="absolute flex items-center justify-center rounded-full" style={{ right: -2, bottom: -2, width: 16, height: 16, background: '#1c1c1e', color: FARBE.gold }} aria-hidden>
              <UsersThree size={10} weight="fill" />
            </span>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{gruppenName(gruppe)}</div>
          {kurzListe && <div className="truncate" style={{ fontSize: 11.5, color: 'rgba(235,235,245,0.55)' }}>{kurzListe}</div>}
          <div className="truncate md:hidden" style={{ fontSize: 10.5, color: 'rgba(235,235,245,0.4)' }}>wird nach {AUFBEWAHRUNG_TAGE} Tagen gelöscht</div>
        </div>
        {/* Gleicher Hinweis wie im 1:1-Kopf — die 7-Tage-Regel gilt für Gruppen genauso, und
            gerade im Team-Chat läuft schnell echte Absprache, die man fürs Ticket halten könnte. */}
        <span className="hidden shrink-0 whitespace-nowrap md:inline" style={{ fontSize: 10.5, color: 'rgba(235,235,245,0.4)' }}>
          wird nach {AUFBEWAHRUNG_TAGE} Tagen gelöscht
        </span>
        <button
          type="button"
          onClick={() => setOffen((v) => !v)}
          aria-expanded={offen}
          className="flex shrink-0 cursor-pointer items-center justify-center rounded-full"
          style={{ width: 32, height: 32, border: 'none', background: offen ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)', color: offen ? FARBE.gold : 'rgba(235,235,245,0.7)' }}
          aria-label="Gruppe verwalten"
        >
          <GearSix size={16} weight="fill" />
        </button>
      </div>

      {offen && (
        <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.1)', padding: 8 }}>
          {istTeamChat ? (
            <p className="px-2 py-1.5 text-[11px] leading-snug text-white/40">
              Mitglieder kommen aus dem Team „{gruppe.team_name}" — Änderungen macht die Team-Verwaltung.
            </p>
          ) : (
            <>
              {darfVerwalten && (
                <div className="mb-2 flex gap-1.5 px-1">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name der Gruppe"
                    className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-[13px] text-white placeholder:text-white/25 focus:border-[#0a84ff]/60 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => void speichern()}
                    disabled={!name.trim() || laedt}
                    className="shrink-0 rounded-lg px-2.5 py-1.5 text-[12px] font-bold text-white disabled:opacity-30" style={{ background: FARBE.blau }}
                  >
                    Speichern
                  </button>
                </div>
              )}

              <div className="mb-1 px-2 text-[10px] uppercase tracking-[0.06em] text-white/30">Mitglieder</div>
              {gruppe.mitglieder.map((id) => (
                <div key={id} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                  <Avatar name={mitgliedName(id)} bild={namen.get(id)?.avatar_url} groesse={28} />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-white/80">{mitgliedName(id)}</span>
                  {darfVerwalten && id !== meineId && (
                    <button
                      type="button"
                      onClick={() => void entfernen(id)}
                      disabled={laedt}
                      className="shrink-0 text-white/30 hover:text-red-300 disabled:opacity-30"
                      aria-label={`${mitgliedName(id)} entfernen`}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}

              {darfVerwalten && hinzufuegbar.length > 0 && (
                <>
                  <div className="mb-1 mt-2 px-2 text-[10px] uppercase tracking-[0.06em] text-white/30">Hinzufügen</div>
                  {hinzufuegbar.map((k) => (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => void hinzufuegen(k.id)}
                      disabled={laedt}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-white/[0.04] disabled:opacity-30"
                    >
                      <Avatar name={kollegeName(k)} bild={k.avatar_url} groesse={28} />
                      <span className="truncate text-[13px] text-white/80">{kollegeName(k)}</span>
                    </button>
                  ))}
                </>
              )}

              {darfVerlassen && (
                <button
                  type="button"
                  onClick={() => void verlassen()}
                  disabled={laedt}
                  className="mt-2 w-full rounded-lg px-2 py-1.5 text-center text-[12px] text-red-300/80 hover:bg-red-400/10 hover:text-red-200 disabled:opacity-30"
                >
                  Gruppe verlassen
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
